import React, {FC, useContext} from 'react';
import {Button, Typography} from 'onboarding-components';
import clsx from 'clsx';

import {IMMERSIVE_ASSETS} from 'common/onboarding/immersive-onboarding/assets';
import {ImplementMethodButton} from 'common/onboarding/immersive-onboarding/components/implement-method-button';
import {ImplementMethodGrid} from 'common/onboarding/immersive-onboarding/components/implement-method-grid';
import {TrackingEvent, TrackingProperty, trackOnboardingEvent} from 'common/onboarding/immersive-onboarding/tracking';
import {handleCompleteStep} from 'common/onboarding/immersive-onboarding/utils';
import {SetupGuideContext, Step} from 'common/onboarding/immersive-onboarding/types';
import {useTranslation} from 'common/i18n/hooks';
import common from 'iron/common/onboarding/immersive-onboarding/steps/common.module.styl';

export const AlternateSDKs: FC = () => {
  const {setStep, setupGuideInfo, setSetupGuideInfo, setSelectAltSDK} = useContext(SetupGuideContext);
  const {t} = useTranslation();

  const handleImplementMethodClick = (buttonTitle: string) => {
    trackOnboardingEvent(TrackingEvent.LinkClicked, {
      [TrackingProperty.LinkSource]: buttonTitle,
    });
  };

  const handleBackToSDK = () => {
    handleCompleteStep({
      setupGuideInfo,
      setSetupGuideInfo,
      setStep,
      completedStep: Step.GetStarted,
      nextStep: Step.Connect,
      trackingEvent: TrackingEvent.StartWithSDKClick,
    });

    setSelectAltSDK(false);
  };

  const handleVerifyConnection = () => {
    handleCompleteStep({
      setupGuideInfo,
      setSetupGuideInfo,
      setStep,
      completedStep: Step.Connect,
      nextStep: Step.Verify,
      trackingEvent: TrackingEvent.VerifyConnection,
    });

    setSelectAltSDK(true);
  };

  return (
    <div className={common.container}>
      <header className={common.mb16}>
        <Typography className={clsx(common.mt0, common.mb8)} variant='headingSm'>
          {t(`onboarding.connect.select-sdk`)}
        </Typography>
        <Typography variant='bodySm'>{t(`onboarding.connect.select-sdk-description`)}</Typography>
      </header>
      <ImplementMethodGrid className={common.mt42}>
        <ImplementMethodButton
          title={`Javascript`}
          url='https://docs.mixpanel.com/docs/tracking-methods/sdks/javascript'
          imgSrc={IMMERSIVE_ASSETS.plainJavascript}
          highlight={t(`onboarding.implement.popular`)}
          onClick={() => handleImplementMethodClick(TrackingEvent.JavascriptDocLink)}
        />
        <ImplementMethodButton
          title={`React Native`}
          url='https://docs.mixpanel.com/docs/tracking-methods/sdks/react-native'
          imgSrc={IMMERSIVE_ASSETS.reactNative}
          onClick={() => handleImplementMethodClick(TrackingEvent.ReactNativeDocLink)}
        />
        <ImplementMethodButton
          title={`NodeJS`}
          url='https://docs.mixpanel.com/docs/tracking-methods/sdks/nodejs'
          imgSrc={IMMERSIVE_ASSETS.nodeJS}
          onClick={() => handleImplementMethodClick(TrackingEvent.NodeDocLink)}
        />
        <ImplementMethodButton
          title={`Python`}
          url='https://docs.mixpanel.com/docs/tracking-methods/sdks/python'
          imgSrc={IMMERSIVE_ASSETS.python}
          onClick={() => handleImplementMethodClick(TrackingEvent.PythonDocLink)}
        />
        <ImplementMethodButton
          title={`iOS (Swift)`}
          url='https://docs.mixpanel.com/docs/tracking-methods/sdks/swift'
          imgSrc={IMMERSIVE_ASSETS.ios}
          onClick={() => handleImplementMethodClick(TrackingEvent.SwiftDocLink)}
        />
        <ImplementMethodButton
          title={t(`onboarding.implement.more-options`)}
          url='https://docs.mixpanel.com/docs/tracking-methods/sdks'
          imgSrc={IMMERSIVE_ASSETS.more}
          onClick={() => handleImplementMethodClick(TrackingEvent.MoreSdksDocLink)}
        />
      </ImplementMethodGrid>
      <div className={clsx(common.buttonGroup, common.mt24)}>
        <Button variant='transparent' size='small' onClick={handleBackToSDK}>
          {t(`onboarding.sidenav.go-back`)}
        </Button>
        <Button variant='solid' size='small' onClick={handleVerifyConnection}>
          {t(`onboarding.sidenav.verify-connection`)}
        </Button>
      </div>
    </div>
  );
};
